import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import type { AuthUser } from "../../shared/types.js";
import {
  mapAuthApiError,
  maskEmail,
} from "../lib/auth-state.js";
import {
  selectTurnstileSize,
  type TurnstileRenderSize,
} from "../lib/turnstile-size.js";

type Step = "email" | "code";

type PublicConfig = {
  turnstileSiteKey?: string;
};

type RequestCodeOk = {
  ok: true;
  challengeId: string;
  expiresIn: number;
  resendAfter: number;
};

type AuthScreenProps = {
  onAuthenticated: (user: AuthUser) => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          size?: "normal" | "flexible" | "compact";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export function AuthScreen({ onAuthenticated }: AuthScreenProps) {
  const emailFieldId = useId();
  const codeFieldId = useId();
  const statusId = useId();
  const turnstileHostRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState(600);
  const [resendAfter, setResendAfter] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [siteKey, setSiteKey] = useState("");
  const [turnstileSize, setTurnstileSize] = useState<TurnstileRenderSize>("flexible");

  // Show whenever the public site key is configured (including localhost dogfood).
  // Server still skips siteverify when APP_URL is loopback so CLI acceptance stays token-free.
  const showTurnstile = Boolean(siteKey);

  useEffect(() => {
    void fetch("/api/config/public")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: PublicConfig | null) => {
        setSiteKey(data?.turnstileSiteKey?.trim() ?? "");
      })
      .catch(() => setSiteKey(""));
  }, []);

  useEffect(() => {
    const host = turnstileHostRef.current;
    if (!host || !showTurnstile) return;

    const updateSize = () => {
      const next = selectTurnstileSize(host.clientWidth);
      setTurnstileSize((prev) => (prev === next ? prev : next));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(host);
    return () => observer.disconnect();
  }, [showTurnstile]);

  useEffect(() => {
    if (!showTurnstile || !turnstileHostRef.current) return;
    let cancelled = false;

    function mountWidget() {
      if (cancelled || !turnstileHostRef.current || !window.turnstile) return;
      if (turnstileWidgetId.current) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
      turnstileWidgetId.current = window.turnstile.render(turnstileHostRef.current, {
        sitekey: siteKey,
        size: turnstileSize,
        callback: (token) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(null),
        "expired-callback": () => setTurnstileToken(null),
      });
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-dfib-turnstile="1"]',
    );
    if (window.turnstile) {
      mountWidget();
    } else if (existing) {
      existing.addEventListener("load", mountWidget);
    } else {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.dataset.dfibTurnstile = "1";
      script.addEventListener("load", mountWidget);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [showTurnstile, siteKey, turnstileSize]);

  useEffect(() => {
    if (resendAfter <= 0) return;
    const t = window.setInterval(() => {
      setResendAfter((n) => Math.max(0, n - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [resendAfter]);

  useEffect(() => {
    if (step === "code") {
      codeInputRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
  }, [step]);

  function resetTurnstile() {
    if (showTurnstile && turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken(null);
  }

  async function requestCode(nextEmail: string) {
    if (showTurnstile && !turnstileToken) {
      setStatus("Complete the bot check to continue.");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch("/api/auth/request-code", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: nextEmail,
          turnstileToken: turnstileToken ?? undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as RequestCodeOk & {
        error?: string;
        resendAfter?: number;
      };
      if (!res.ok) {
        if (typeof data.resendAfter === "number") {
          setResendAfter(data.resendAfter);
        }
        setStatus(mapAuthApiError(data.error));
        resetTurnstile();
        return;
      }
      setEmail(nextEmail);
      setChallengeId(data.challengeId);
      setExpiresIn(data.expiresIn);
      setResendAfter(data.resendAfter);
      setCode("");
      setStep("code");
      setStatus("Code sent. Check your inbox.");
      // Tokens are single-use; require a fresh widget solve before resend.
      resetTurnstile();
    } catch {
      setStatus("Could not reach the server. Check your connection.");
    } finally {
      setBusy(false);
    }
  }

  async function onEmailSubmit(e: FormEvent) {
    e.preventDefault();
    const next = email.trim().toLowerCase();
    if (!next) {
      setStatus("Enter a valid email address.");
      return;
    }
    await requestCode(next);
  }

  async function onCodeSubmit(e: FormEvent) {
    e.preventDefault();
    if (!challengeId) {
      setStatus("Request a new code first.");
      setStep("email");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch("/api/auth/verify-code", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          challengeId,
          email,
          code: code.replace(/\s+/g, ""),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        user?: AuthUser;
        error?: string;
      };
      if (!res.ok || !data.user) {
        setStatus(mapAuthApiError(data.error));
        return;
      }
      onAuthenticated(data.user);
    } catch {
      setStatus("Could not reach the server. Check your connection.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel auth-panel" aria-labelledby="auth-heading">
      <h2 id="auth-heading">Sign in</h2>
      <p className="auth-lead">
        Enter your email for a one-time code. New addresses create an account when the
        code is verified.
      </p>

      {step === "email" ? (
        <form className="auth-form" onSubmit={(e) => void onEmailSubmit(e)}>
          <label className="auth-label" htmlFor={emailFieldId}>
            Email
          </label>
          <input
            ref={emailInputRef}
            id={emailFieldId}
            className="auth-input"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            disabled={busy}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="row">
            <button
              type="submit"
              className="btn"
              disabled={busy || (showTurnstile && !turnstileToken)}
            >
              {busy ? "Sending…" : "Email me a code"}
            </button>
          </div>
        </form>
      ) : (
        <form className="auth-form" onSubmit={(e) => void onCodeSubmit(e)}>
          <p className="auth-dest">
            Code sent to <strong>{maskEmail(email)}</strong>. Expires in about{" "}
            {Math.max(1, Math.round(expiresIn / 60))} minutes.
          </p>
          <label className="auth-label" htmlFor={codeFieldId}>
            One-time code
          </label>
          <input
            ref={codeInputRef}
            id={codeFieldId}
            className="auth-input auth-code"
            type="text"
            name="code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            disabled={busy}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <div className="row">
            <button
              type="submit"
              className="btn"
              disabled={busy || code.length !== 6}
            >
              {busy ? "Verifying…" : "Verify code"}
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={busy || resendAfter > 0 || (showTurnstile && !turnstileToken)}
              onClick={() => void requestCode(email)}
            >
              {resendAfter > 0 ? `Resend in ${resendAfter}s` : "Resend code"}
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() => {
                setStep("email");
                setChallengeId(null);
                setCode("");
                setStatus(null);
              }}
            >
              Use a different email
            </button>
          </div>
        </form>
      )}

      {/* Keep outside step forms so the widget survives email→code and can mint a fresh token for resend. */}
      {showTurnstile ? (
        <div
          className={`auth-turnstile${turnstileSize === "compact" ? " auth-turnstile--compact" : ""}`}
          ref={turnstileHostRef}
          data-turnstile-size={turnstileSize}
        />
      ) : null}

      <p id={statusId} className="auth-status" role="status" aria-live="polite">
        {status ?? ""}
      </p>
    </section>
  );
}
